import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ticketsApi } from '../api/endpoints/tickets';
import type { GetTicketsParams } from '../types/ticket';
import toast from 'react-hot-toast';

export function useTickets() {
  const queryClient = useQueryClient();

  const useList = (params: GetTicketsParams) =>
    useQuery({
      queryKey: ['admin-tickets', params],
      queryFn: () => ticketsApi.getList(params).then((r) => r.data),
    });

  const useDetail = (id: number | null) =>
    useQuery({
      queryKey: ['admin-ticket', id],
      queryFn: () => ticketsApi.getDetail(id!).then((r) => r.data.data),
      enabled: !!id,
    });

  const useStats = () =>
    useQuery({
      queryKey: ['admin-ticket-stats'],
      queryFn: () => ticketsApi.getStats().then((r) => r.data.data),
      refetchInterval: 60_000,
    });

  const useAssign = () =>
    useMutation({
      mutationFn: ({ id, assignedTo }: { id: number; assignedTo: string }) =>
        ticketsApi.assign(id, assignedTo),
      onSuccess: (_, { id }) => {
        toast.success('Ticket assigned');
        queryClient.invalidateQueries({ queryKey: ['admin-ticket', id] });
        queryClient.invalidateQueries({ queryKey: ['admin-tickets'] });
        queryClient.invalidateQueries({ queryKey: ['admin-ticket-stats'] });
      },
      onError: () => toast.error('Failed to assign ticket'),
    });

  const useUpdateStatus = () =>
    useMutation({
      mutationFn: ({ id, status, resolution }: { id: number; status: string; resolution?: string }) =>
        ticketsApi.updateStatus(id, status, resolution),
      onSuccess: (_, { id }) => {
        toast.success('Ticket status updated');
        queryClient.invalidateQueries({ queryKey: ['admin-ticket', id] });
        queryClient.invalidateQueries({ queryKey: ['admin-tickets'] });
        queryClient.invalidateQueries({ queryKey: ['admin-ticket-stats'] });
      },
      onError: () => toast.error('Failed to update ticket status'),
    });

  const useAddComment = () =>
    useMutation({
      mutationFn: ({
        id,
        comment,
        isInternal,
        attachmentsJson,
      }: {
        id: number;
        comment: string;
        isInternal: boolean;
        attachmentsJson?: string;
      }) => ticketsApi.addComment(id, comment, isInternal, attachmentsJson),
      onSuccess: (_, { id }) => {
        toast.success('Comment added');
        queryClient.invalidateQueries({ queryKey: ['admin-ticket', id] });
      },
      onError: () => toast.error('Failed to add comment'),
    });

  return { useList, useDetail, useStats, useAssign, useUpdateStatus, useAddComment };
}
